import { z } from 'zod';

import {
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  ulidSchema,
} from '../common';
import {
  agentCommandStatusSchema,
  agentStatusSchema,
  browserStatusSchema,
  platformSchema,
  platformSessionStatusSchema,
  projectStatusSchema,
} from '../enums';

/**
 * `/browser-profiles` DTOs — read models plus the control endpoints.
 *
 * A browser profile is a Chromium `userDataDir` on the user's machine, signed
 * into ONE platform (chosen at registration). The server knows its id, its
 * label, its platform and the state the agent reported; it never receives the
 * directory, the cookies, or any credential. The display name and avatar below
 * come from `platform.session_status`, which the agent populates from the
 * rendered page — that is the whole of what leaves the client about a session.
 *
 * The control endpoints do not act on the browser. They enqueue one command
 * from the closed allowlist below, stamped with a `commandId` and an
 * `expiresAt`, and the agent executes it idempotently by `commandId`. There is
 * no endpoint that forwards a script, a shell string or a file path — such a
 * command is not representable in this type space.
 */

export const browserProfileSchema = z.object({
  id: idSchema,
  agentId: idSchema,
  /** Operator-chosen label, e.g. "FB test account". Never the profile path. */
  name: z.string(),
  /** The one channel this Chromium profile is signed into. A project may only
   *  be assigned a profile of its own platform. */
  platform: platformSchema,
  status: browserStatusSchema,
  sessionStatus: platformSessionStatusSchema,
  /** Reported by the agent from the logged-in page; no cookie or token is sent. */
  displayName: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  /** At most one project per profile at a time. */
  assignedProjectId: idSchema.nullable(),
  assignedProjectName: z.string().nullable(),
  /**
   * Status of the assigned project. The assignment is exclusive only while this
   * is one of `PROFILE_HOLDING_PROJECT_STATUSES`; a profile whose project has
   * stopped may be taken over by another project's Start. Null iff
   * `assignedProjectId` is null.
   */
  assignedProjectStatus: projectStatusSchema.nullable(),
  /** True while a headed window is open for the user to interact with. */
  interactiveOpen: z.boolean(),
  statusMessage: z.string().nullable(),
  lastStatusAt: isoDateTimeSchema.nullable(),
  /**
   * When the agent last MEASURED the platform session — distinct from
   * `lastStatusAt`, which tracks the browser lifecycle.
   *
   * Without it `sessionStatus: 'UNKNOWN'` is unreadable: "never checked" and
   * "checked and could not tell" look identical, and a `LOGGED_IN` from three
   * days ago looks as fresh as one from a minute ago. Null means the session has
   * never been measured for this profile — which is NOT the same as logged out
   * and must never be rendered as such.
   */
  lastSessionCheckAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type BrowserProfile = z.infer<typeof browserProfileSchema>;

export const browserProfileListQuerySchema = paginationQuerySchema.extend({
  agentId: idSchema.optional(),
  projectId: idSchema.optional(),
  platform: platformSchema.optional(),
  status: z.union([browserStatusSchema, z.array(browserStatusSchema)]).optional(),
  sessionStatus: platformSessionStatusSchema.optional(),
});
export type BrowserProfileListQuery = z.infer<typeof browserProfileListQuerySchema>;

export const browserProfileListResponseSchema = paginated(browserProfileSchema);
export type BrowserProfileListResponse = z.infer<typeof browserProfileListResponseSchema>;

/** Recent command trail, so an operator can see why a profile is not moving. */
export const browserCommandSummarySchema = z.object({
  commandId: ulidSchema,
  type: z.string(),
  status: agentCommandStatusSchema,
  issuedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
  acknowledgedAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  errorMessage: z.string().nullable(),
});
export type BrowserCommandSummary = z.infer<typeof browserCommandSummarySchema>;

export const browserProfileDetailResponseSchema = z.object({
  profile: browserProfileSchema,
  agent: z.object({
    id: idSchema,
    name: z.string(),
    status: agentStatusSchema,
    lastHeartbeatAt: isoDateTimeSchema.nullable(),
  }),
  recentCommands: z.array(browserCommandSummarySchema),
  /** Present when the session needs a human: checkpoint, 2FA, restriction. The
   *  system stops here by design and never attempts to solve or bypass it. */
  userActionRequired: z
    .object({
      sessionStatus: platformSessionStatusSchema,
      detectedAt: isoDateTimeSchema,
      instruction: z.string(),
    })
    .nullable(),
});
export type BrowserProfileDetailResponse = z.infer<typeof browserProfileDetailResponseSchema>;

// ---------------------------------------------------------------------------
// Control — POST /browser-profiles/:id/control
// ---------------------------------------------------------------------------

/**
 * The closed set of browser actions the dashboard may request.
 *
 * There is deliberately no `show` / `hide` pair. Toggling Playwright's
 * `headless` flag on a live browser is not possible: switching between headed
 * and headless requires closing the context and launching a new one, and a
 * single `userDataDir` may only be open in one process at a time. Pretending
 * otherwise would either corrupt the profile or silently do nothing. So the
 * visible-window lifecycle is expressed honestly as two separate transitions:
 *
 *   open_interactive  → close any headless context, launch a headed one
 *   close_interactive → close the headed window, optionally relaunch headless
 *
 * `bring_to_front` and `minimize` only move an already-open headed window and
 * are Windows-native operations; they never change the headless mode.
 */
export const BROWSER_CONTROL_ACTIONS = [
  'open_login',
  'open_interactive',
  'close_interactive',
  'bring_to_front',
  'minimize',
  'start',
  'stop',
] as const;
export const browserControlActionSchema = z.enum(BROWSER_CONTROL_ACTIONS);
export type BrowserControlAction = (typeof BROWSER_CONTROL_ACTIONS)[number];

/** Wire names of the same actions on the Server→Agent WebSocket allowlist. */
export const BROWSER_CONTROL_COMMAND_TYPES = [
  'browser.open_login',
  'browser.open_interactive',
  'browser.close_interactive',
  'browser.bring_to_front',
  'browser.minimize',
  'browser.start',
  'browser.stop',
] as const;
export const browserControlCommandTypeSchema = z.enum(BROWSER_CONTROL_COMMAND_TYPES);
export type BrowserControlCommandType = (typeof BROWSER_CONTROL_COMMAND_TYPES)[number];

/** REST action → allowlisted command name. Exhaustive by construction. */
export const BROWSER_ACTION_COMMAND_MAP: Readonly<
  Record<BrowserControlAction, BrowserControlCommandType>
> = {
  open_login: 'browser.open_login',
  open_interactive: 'browser.open_interactive',
  close_interactive: 'browser.close_interactive',
  bring_to_front: 'browser.bring_to_front',
  minimize: 'browser.minimize',
  start: 'browser.start',
  stop: 'browser.stop',
} as const;

export const browserControlRequestSchema = z.object({
  action: browserControlActionSchema,
  /** Binds `start`/`stop` to the project the profile is assigned to. */
  projectId: idSchema.optional(),
  /** After `close_interactive`, relaunch a headless context for the assigned
   *  project instead of leaving the profile stopped. */
  relaunchHeadless: z.boolean().default(false),
  reason: z.string().trim().max(500).optional(),
});
export type BrowserControlRequest = z.infer<typeof browserControlRequestSchema>;

/**
 * Accepted, not done. The command is queued with an expiry; the agent reports
 * the real outcome over WebSocket and the dashboard updates from there.
 */
export const browserCommandResponseSchema = z.object({
  commandId: ulidSchema,
  type: browserControlCommandTypeSchema,
  status: agentCommandStatusSchema,
  browserProfileId: idSchema,
  agentId: idSchema,
  issuedAt: isoDateTimeSchema,
  /** After this instant the agent must drop the command unexecuted. */
  expiresAt: isoDateTimeSchema,
});
export type BrowserCommandResponse = z.infer<typeof browserCommandResponseSchema>;

// ---------------------------------------------------------------------------
// Profile creation — POST /browser-profiles
// ---------------------------------------------------------------------------

/**
 * Register a Chromium profile against a paired agent.
 *
 * The server allocates the row; the directory itself is created by the agent on
 * first launch, under its own profiles folder. That split is why
 * `profileDirectoryName` is a NAME and not a path, and why the pattern below
 * excludes `/`, `\` and `.` runs: it is the one string the server sends that the
 * client turns into a filesystem location, so a traversal here would let the
 * server read or overwrite arbitrary files on a user's machine. Omit it and the
 * server derives a safe name from the row id.
 */
export const createBrowserProfileRequestSchema = z.object({
  agentId: idSchema,
  /** Operator-chosen label, e.g. "FB test account". */
  name: z.string().trim().min(1).max(120),
  /** The channel this profile will sign into. Immutable after creation: the
   *  Chromium directory accumulates one platform's session, and rebinding it
   *  would present another site's automation with foreign cookies. */
  platform: platformSchema.default('FACEBOOK'),
  profileDirectoryName: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9_-]*$/,
      'Chỉ gồm chữ, số, gạch dưới và gạch ngang; không chứa dấu chấm hay dấu phân cách thư mục.',
    )
    .optional(),
});
export type CreateBrowserProfileRequest = z.infer<typeof createBrowserProfileRequestSchema>;

export const createBrowserProfileResponseSchema = browserProfileSchema;
export type CreateBrowserProfileResponse = z.infer<typeof createBrowserProfileResponseSchema>;

// ---------------------------------------------------------------------------
// Profile assignment — PATCH /browser-profiles/:id
// ---------------------------------------------------------------------------

export const updateBrowserProfileRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    /** `null` releases the profile; a profile already bound to a running
     *  project is rejected with CONFLICT rather than silently reassigned. */
    assignedProjectId: idSchema.nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateBrowserProfileRequest = z.infer<typeof updateBrowserProfileRequestSchema>;

export const updateBrowserProfileResponseSchema = browserProfileSchema;
export type UpdateBrowserProfileResponse = z.infer<typeof updateBrowserProfileResponseSchema>;
